export type ResourceCapacity = {
  cpuCores: number
  ramMB: number
  diskGB: number
}

export type ResourceCompatibility = {
  required: ResourceCapacity
  available: ResourceCapacity
  cpuInsufficient: boolean
  ramInsufficient: boolean
  diskInsufficient: boolean
  insufficient: boolean
}

type MinimumResourceUnitCountOptions = {
  required: ResourceCapacity
  unitCapacity: ResourceCapacity
  extraCapacity?: ResourceCapacity
  supportsExtraResources: boolean
  currentCount?: number
  maxCount: number
  step?: number
}

const EMPTY_RESOURCE_CAPACITY: ResourceCapacity = {
  cpuCores: 0,
  ramMB: 0,
  diskGB: 0,
}

function normalizeResourceCapacity(capacity: ResourceCapacity): ResourceCapacity {
  return {
    cpuCores: Number.isFinite(capacity.cpuCores) && capacity.cpuCores > 0 ? capacity.cpuCores : 0,
    ramMB: Number.isFinite(capacity.ramMB) && capacity.ramMB > 0 ? capacity.ramMB : 0,
    diskGB: Number.isFinite(capacity.diskGB) && capacity.diskGB > 0 ? capacity.diskGB : 0,
  }
}

export function getResourceCompatibility({
  required,
  baseCapacity,
  extraCapacity = EMPTY_RESOURCE_CAPACITY,
  supportsExtraResources,
}: {
  required: ResourceCapacity
  baseCapacity: ResourceCapacity
  extraCapacity?: ResourceCapacity
  supportsExtraResources: boolean
}): ResourceCompatibility {
  const normalizedRequired = normalizeResourceCapacity(required)
  const normalizedBaseCapacity = normalizeResourceCapacity(baseCapacity)
  const normalizedExtraCapacity = supportsExtraResources
    ? normalizeResourceCapacity(extraCapacity)
    : EMPTY_RESOURCE_CAPACITY
  const available = {
    cpuCores: normalizedBaseCapacity.cpuCores + normalizedExtraCapacity.cpuCores,
    ramMB: normalizedBaseCapacity.ramMB + normalizedExtraCapacity.ramMB,
    diskGB: normalizedBaseCapacity.diskGB + normalizedExtraCapacity.diskGB,
  }
  const cpuInsufficient = normalizedRequired.cpuCores > available.cpuCores
  const ramInsufficient = normalizedRequired.ramMB > available.ramMB
  const diskInsufficient = normalizedRequired.diskGB > available.diskGB

  return {
    required: normalizedRequired,
    available,
    cpuInsufficient,
    ramInsufficient,
    diskInsufficient,
    insufficient: cpuInsufficient || ramInsufficient || diskInsufficient,
  }
}

export function getMinimumResourceUnitCount({
  required,
  unitCapacity,
  extraCapacity = EMPTY_RESOURCE_CAPACITY,
  supportsExtraResources,
  currentCount = 1,
  maxCount,
  step = 1,
}: MinimumResourceUnitCountOptions): number | undefined {
  const normalizedRequired = normalizeResourceCapacity(required)
  const normalizedUnitCapacity = normalizeResourceCapacity(unitCapacity)
  const normalizedExtraCapacity = supportsExtraResources
    ? normalizeResourceCapacity(extraCapacity)
    : EMPTY_RESOURCE_CAPACITY
  const normalizedStep = Number.isFinite(step) && step > 0 ? Math.max(Math.floor(step), 1) : 1
  const normalizedCurrentCount =
    Number.isFinite(currentCount) && currentCount > 0 ? currentCount : normalizedStep
  const alignedCurrentCount =
    Math.ceil(Math.max(normalizedCurrentCount, normalizedStep) / normalizedStep) * normalizedStep
  const alignedMaxCount =
    Number.isFinite(maxCount) && maxCount > 0
      ? Math.floor(maxCount / normalizedStep) * normalizedStep
      : 0

  if (alignedCurrentCount > alignedMaxCount) return undefined

  const getRequiredUnitCount = (
    requiredAmount: number,
    extraAmount: number,
    unitAmount: number,
  ) => {
    const remainingAmount = Math.max(requiredAmount - extraAmount, 0)

    if (remainingAmount <= 0) return 0
    if (unitAmount <= 0) return Number.POSITIVE_INFINITY

    return Math.ceil(remainingAmount / unitAmount)
  }
  const requiredCount = Math.max(
    alignedCurrentCount,
    getRequiredUnitCount(
      normalizedRequired.cpuCores,
      normalizedExtraCapacity.cpuCores,
      normalizedUnitCapacity.cpuCores,
    ),
    getRequiredUnitCount(
      normalizedRequired.ramMB,
      normalizedExtraCapacity.ramMB,
      normalizedUnitCapacity.ramMB,
    ),
    getRequiredUnitCount(
      normalizedRequired.diskGB,
      normalizedExtraCapacity.diskGB,
      normalizedUnitCapacity.diskGB,
    ),
  )
  const alignedRequiredCount = Math.ceil(requiredCount / normalizedStep) * normalizedStep

  if (!Number.isFinite(alignedRequiredCount) || alignedRequiredCount > alignedMaxCount) {
    return undefined
  }

  const compatibility = getResourceCompatibility({
    required: normalizedRequired,
    baseCapacity: {
      cpuCores: normalizedUnitCapacity.cpuCores * alignedRequiredCount,
      ramMB: normalizedUnitCapacity.ramMB * alignedRequiredCount,
      diskGB: normalizedUnitCapacity.diskGB * alignedRequiredCount,
    },
    extraCapacity: normalizedExtraCapacity,
    supportsExtraResources,
  })

  return compatibility.insufficient ? undefined : alignedRequiredCount
}
